import { useCallback, useEffect, useRef, useState } from "react";
import api from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { toast } from "sonner";
import { MapPin, Globe2, Target, Loader2 } from "lucide-react";
import { APIProvider, Map, AdvancedMarker, InfoWindow } from "@vis.gl/react-google-maps";

const MAPS_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
const RADIUS_CHIPS = [4, 8, 15, 30];

/**
 * Iter 77 — Rental location filter + list for Trigo Tour.
 *
 * Three modes:
 *   - "nearby":  request browser geolocation, show vehicles within the
 *                admin-configured radius (default 4 km) whose driver is
 *                online and GPS-active.
 *   - "custom":  passenger picks an address via Google Places; same
 *                radius filter applied around that point.
 *   - "all":     every rental vehicle, geo filter disabled.
 *
 * The component exposes the filtered list through `renderVehicle`
 * so the parent still owns the card layout.
 */
export default function RentalVehiclesTab({ renderVehicle, emptyMessage = "Aucun vehicule disponible", onVehicleClick }) {
  if (!MAPS_KEY) return <RentalVehiclesTabInner renderVehicle={renderVehicle} emptyMessage={emptyMessage} onVehicleClick={onVehicleClick} />;
  return (
    <APIProvider apiKey={MAPS_KEY} libraries={["places", "marker"]}>
      <RentalVehiclesTabInner renderVehicle={renderVehicle} emptyMessage={emptyMessage} onVehicleClick={onVehicleClick} />
    </APIProvider>
  );
}

function RentalVehiclesTabInner({ renderVehicle, emptyMessage, onVehicleClick }) {
  const [mode, setMode] = useState("nearby"); // "nearby" | "custom" | "all"
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [adminRadius, setAdminRadius] = useState(4);
  const [radius, setRadius] = useState(4);
  const [coords, setCoords] = useState(null); // { lat, lng, label }
  const [geoDenied, setGeoDenied] = useState(false);
  const [customInput, setCustomInput] = useState("");
  const [selectedMarker, setSelectedMarker] = useState(null);
  const customInputRef = useRef(null);
  const autoRef = useRef(null);

  // Pull the admin-configured radius once so the UI label stays accurate.
  useEffect(() => {
    api.get("/rental-config")
      .then(({ data }) => {
        const r = Number(data.radius_km) || 4;
        setAdminRadius(r);
        setRadius(r);
      })
      .catch(() => { /* fallback 4 */ });
  }, []);

  // Ask the browser for the current GPS fix on first mount so the
  // default "Autour de moi" mode has something to filter on.
  useEffect(() => {
    if (!navigator.geolocation) {
      setGeoDenied(true);
      setMode("all");
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setCoords({ lat: pos.coords.latitude, lng: pos.coords.longitude, label: "Ma position" });
      },
      () => {
        setGeoDenied(true);
        setMode("all");
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 },
    );
  }, []);

  // Attach a Google Places Autocomplete to the custom-location input the
  // first time the user switches to that mode (lazy so the Maps JS lib
  // has had time to load from APIProvider higher up in the tree).
  useEffect(() => {
    if (mode !== "custom" || !customInputRef.current) return;
    if (autoRef.current) return;
    let cancelled = false;
    const tryAttach = () => {
      if (cancelled) return;
      if (!window.google?.maps?.places) {
        setTimeout(tryAttach, 300);
        return;
      }
      const auto = new window.google.maps.places.Autocomplete(customInputRef.current, {
        fields: ["geometry", "formatted_address", "name"],
      });
      auto.addListener("place_changed", () => {
        const p = auto.getPlace();
        if (p?.geometry?.location) {
          setCoords({
            lat: p.geometry.location.lat(),
            lng: p.geometry.location.lng(),
            label: p.formatted_address || p.name || "Lieu choisi",
          });
          setCustomInput(p.formatted_address || p.name || "");
        }
      });
      autoRef.current = auto;
    };
    tryAttach();
    return () => { cancelled = true; };
  }, [mode]);

  const fetchVehicles = useCallback(async () => {
    setLoading(true);
    try {
      const params = { mode };
      if ((mode === "nearby" || mode === "custom") && coords) {
        params.lat = coords.lat;
        params.lng = coords.lng;
        params.radius_km = radius;
      } else if (mode !== "all") {
        // waiting for coords — don't fire a bad request
        setLoading(false);
        return;
      }
      const { data } = await api.get("/rental/vehicles-nearby", { params });
      setVehicles(data.vehicles || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur lors du chargement");
    } finally {
      setLoading(false);
    }
  }, [mode, coords, radius]);

  useEffect(() => { fetchVehicles(); }, [fetchVehicles]);

  // Clicking the "Autour de moi" chip when GPS was denied re-asks the
  // browser so the passenger isn't stuck in "all" mode forever.
  const requestGeo = () => {
    if (!navigator.geolocation) {
      toast.error("Géolocalisation non supportée par ce navigateur");
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setCoords({ lat: pos.coords.latitude, lng: pos.coords.longitude, label: "Ma position" });
        setGeoDenied(false);
        setMode("nearby");
      },
      () => {
        setGeoDenied(true);
        toast.error("Impossible d'obtenir votre position. Autorisez l'accès dans votre navigateur.");
      },
      { enableHighAccuracy: true, timeout: 10000 },
    );
  };

  const modeBtn = (key, Icon, label, testid) => {
    const active = mode === key;
    return (
      <button
        key={key}
        onClick={() => {
          if (key === "nearby" && !coords) { requestGeo(); return; }
          setMode(key);
        }}
        className={`flex items-center gap-2 px-3 py-2 rounded-full text-xs sm:text-sm border transition-all ${
          active
            ? "bg-[#0A2540] text-white border-[#0A2540] shadow"
            : "bg-white text-[#0A2540] border-[#CBD5E1] hover:border-[#FACC15]"
        }`}
        data-testid={testid}
      >
        <Icon className="w-3.5 h-3.5" />
        {label}
      </button>
    );
  };

  return (
    <div>
      <div className="bg-white rounded-xl border border-[#CBD5E1] p-3 sm:p-4 mb-4" data-testid="rental-location-filter">
        <div className="flex items-center gap-2 mb-3 flex-wrap">
          <MapPin className="w-4 h-4 text-[#0A2540]" />
          <span className="text-sm font-semibold text-[#0A2540]" style={{ fontFamily: "Outfit" }}>
            Localisation
          </span>
          {coords?.label && (mode === "nearby" || mode === "custom") && (
            <span className="text-[11px] text-[#64748B] ml-auto truncate max-w-[60%]">
              📍 {coords.label}
            </span>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          {modeBtn("nearby", Target, `Autour de moi (${radius} km)`, "filter-mode-nearby")}
          {modeBtn("custom", MapPin, "Changer de lieu", "filter-mode-custom")}
          {modeBtn("all", Globe2, "Voir tous les véhicules", "filter-mode-all")}
        </div>
        {(mode === "nearby" || mode === "custom") && (
          <div className="mt-3 flex items-center gap-2 flex-wrap" data-testid="radius-chips">
            <span className="text-[11px] font-semibold text-[#64748B]">Rayon :</span>
            {RADIUS_CHIPS.map((r) => (
              <button
                key={r}
                onClick={() => setRadius(r)}
                className={`px-2.5 py-1 rounded-full text-[11px] font-semibold border transition-all ${
                  radius === r
                    ? "bg-[#FACC15] text-[#0A2540] border-[#FACC15]"
                    : "bg-white text-[#0A2540] border-[#CBD5E1] hover:border-[#FACC15]"
                }`}
                data-testid={`radius-chip-${r}`}
              >
                {r} km
              </button>
            ))}
          </div>
        )}
        {mode === "custom" && (
          <div className="mt-3">
            <Input
              ref={customInputRef}
              value={customInput}
              onChange={(e) => setCustomInput(e.target.value)}
              placeholder="Tapez une adresse, un quartier, une ville…"
              className="h-10 border-[#CBD5E1] text-sm"
              data-testid="filter-custom-location-input"
            />
            {!coords && <p className="text-[11px] text-[#64748B] mt-1">Choisissez un lieu dans la liste pour afficher les véhicules à proximité.</p>}
          </div>
        )}
        {geoDenied && mode !== "all" && (
          <div className="mt-3 text-xs text-[#B45309] bg-[#FEF3C7] border border-[#FDE68A] rounded-lg p-2">
            Géolocalisation refusée. Autorisez l'accès dans votre navigateur, ou utilisez « Voir tous les véhicules ».
          </div>
        )}
      </div>

      {/* Mini-map with vehicle markers — appears whenever we have at
          least one geolocated vehicle (regardless of the current mode). */}
      {MAPS_KEY && (coords || vehicles.some((v) => v.driver_lat && v.driver_lng)) && (
        <div className="relative w-full h-[280px] sm:h-[340px] rounded-xl overflow-hidden border border-[#CBD5E1] mb-4" data-testid="rental-mini-map">
          <Map
            defaultCenter={coords || { lat: vehicles.find((v) => v.driver_lat)?.driver_lat || 3.848, lng: vehicles.find((v) => v.driver_lng)?.driver_lng || 11.5021 }}
            defaultZoom={coords ? 13 : 11}
            gestureHandling="greedy"
            disableDefaultUI={false}
            mapId="rental-mini-map"
          >
            {coords && (
              <AdvancedMarker position={coords} title="Votre position">
                <div className="w-5 h-5 rounded-full bg-[#3B82F6] border-2 border-white shadow-lg" />
              </AdvancedMarker>
            )}
            {vehicles.filter((v) => v.driver_lat && v.driver_lng).map((v) => (
              <AdvancedMarker
                key={v.id}
                position={{ lat: v.driver_lat, lng: v.driver_lng }}
                title={`${v.brand} ${v.model}`}
                onClick={() => setSelectedMarker(v)}
              >
                <div
                  className={`w-7 h-7 rounded-full border-2 border-white shadow-md flex items-center justify-center text-white text-sm cursor-pointer ${
                    v.driver_online ? "bg-[#10B981]" : "bg-[#94A3B8]"
                  }`}
                  data-testid={`rental-marker-${v.id}`}
                >🚗</div>
              </AdvancedMarker>
            ))}
            {selectedMarker && selectedMarker.driver_lat && selectedMarker.driver_lng && (
              <InfoWindow
                position={{ lat: selectedMarker.driver_lat, lng: selectedMarker.driver_lng }}
                onCloseClick={() => setSelectedMarker(null)}
                pixelOffset={[0, -30]}
              >
                <div style={{ fontFamily: "Outfit", minWidth: 180 }}>
                  <p style={{ fontWeight: 700, color: "#0A2540", margin: 0 }}>
                    {selectedMarker.brand} {selectedMarker.model}
                  </p>
                  <p style={{ fontSize: 11, color: "#64748B", margin: "4px 0 0" }}>
                    {selectedMarker.price_per_day?.toLocaleString()} XAF/jour
                  </p>
                  {typeof selectedMarker.distance_km === "number" && (
                    <p style={{ fontSize: 11, color: "#3B82F6", fontWeight: 600, margin: "2px 0 0" }}>
                      📍 {selectedMarker.distance_km.toFixed(1)} km
                    </p>
                  )}
                  <button
                    onClick={() => { onVehicleClick?.(selectedMarker); setSelectedMarker(null); }}
                    style={{
                      marginTop: 8, padding: "4px 10px", borderRadius: 6,
                      background: "#FACC15", color: "#0A2540", fontSize: 11, fontWeight: 700, border: 0, cursor: "pointer",
                    }}
                    data-testid={`marker-see-${selectedMarker.id}`}
                  >
                    Voir le détail
                  </button>
                </div>
              </InfoWindow>
            )}
          </Map>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-12 text-[#64748B]">
          <Loader2 className="w-5 h-5 animate-spin mr-2" /> Chargement…
        </div>
      ) : vehicles.length === 0 ? (
        <div className="bg-white rounded-xl border border-[#CBD5E1] p-6 text-center text-[#64748B]" data-testid="rental-empty-state">
          <p>{mode === "nearby" || mode === "custom"
            ? `Aucun véhicule disponible dans un rayon de ${radius} km.`
            : emptyMessage}</p>
          {(mode === "nearby" || mode === "custom") && (
            <Button variant="outline" size="sm" className="mt-3 border-[#CBD5E1]" onClick={() => setMode("all")} data-testid="see-all-fallback">
              Voir tous les véhicules
            </Button>
          )}
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="rental-vehicle-grid">
          {vehicles.map((v) => renderVehicle(v))}
        </div>
      )}
    </div>
  );
}
